import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { AppShell } from "@/components/shared/app-shell";
import { SessionTimeoutDialog } from "@/components/shared/dialogs/session-timeout-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/features/auth/use-auth";
import { FindingDemoOverridesProvider } from "@/features/findings/demo/use-finding-demo-overrides";
import { useSessionTimeoutWithCacheClear } from "@/hooks/use-session-timeout";

export const Route = createRootRoute({
  component: RootComponent,
});

const IDLE_MS = 15 * 60 * 1000;
const WARNING_MS = 60 * 1000;

/** Auth routes render their own minimal layout, never the console `AppShell`. */
const PUBLIC_PATHS = new Set([
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/auth/google/callback",
]);

// `AuthProvider` lives in `main.tsx`, outside `RouterProvider` — this
// component only consumes it via `useAuth()`.
function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isPublicRoute = PUBLIC_PATHS.has(pathname);
  const isProfileRoute = pathname === "/profile" || pathname.startsWith("/profile/");
  const isAiRoute = pathname === "/ai" || pathname.startsWith("/ai/");
  const isAiAppDetailRoute = pathname.startsWith("/ai-apps/");
  const isSettingsRoute = pathname === "/settings" || pathname.startsWith("/settings/");
  const { isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isPublicRoute) {
      void navigate({ to: "/login" });
    }
  }, [isLoading, isAuthenticated, isPublicRoute, navigate]);

  // Handles a real race: login/signup call `navigate({ to: "/" })`
  // immediately after their mutation resolves, but that mutation's
  // `setUser` and the router's own pathname update are two independent
  // state changes — if the pathname commits to "/" one render before
  // `isAuthenticated` flips to true, the guard above reads a stale
  // `isAuthenticated=false` and immediately bounces back to `/login`,
  // where it then just sits (there was previously no effect to send an
  // already-authenticated visitor away from a public route). This effect
  // is what actually recovers from that bounce: once `isAuthenticated`
  // genuinely settles true while still on a public route, it retries the
  // navigation into the app.
  useEffect(() => {
    if (!isLoading && isAuthenticated && isPublicRoute) {
      void navigate({ to: "/" });
    }
  }, [isLoading, isAuthenticated, isPublicRoute, navigate]);

  const { isWarning, secondsRemaining, extendSession } = useSessionTimeoutWithCacheClear({
    idleMs: IDLE_MS,
    warningMs: WARNING_MS,
    enabled: isAuthenticated,
    onExpire: () => {
      void logout();
    },
  });

  // Keep demo workflow state at the router root so code-split routes such as
  // Conversations and Findings always share the same provider boundary.
  // Public auth surfaces do not consume this state, but keeping the provider
  // mounted across route changes preserves local demo links during navigation.
  const content = isPublicRoute ? (
    // Public auth surfaces render immediately, with their own layout — no
    // console chrome, no auth-state gate to wait on.
    <Outlet />
  ) : isLoading || !isAuthenticated ? (
    // Resolving the initial session, or about to redirect an unauthenticated
    // visitor away: render a minimal placeholder rather than flashing the
    // authenticated shell (or its content) first.
    <div className="flex min-h-screen items-center justify-center bg-bg-canvas p-8">
      <Skeleton className="h-8 w-40" />
    </div>
  ) : (
    <AppShell
      fullBleed={isProfileRoute || isAiRoute || isAiAppDetailRoute || isSettingsRoute}
    >
      <Outlet />
      <SessionTimeoutDialog
        open={isWarning}
        secondsRemaining={secondsRemaining}
        onStayActive={extendSession}
        onSignOut={() => void logout()}
      />
    </AppShell>
  );

  return <FindingDemoOverridesProvider>{content}</FindingDemoOverridesProvider>;
}
